import { call, put, select, takeLatest } from 'redux-saga/effects';
import { Response } from '@types';
import { AxiosError } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';

import { RequestStatus } from 'shared/constants/State';
import * as actions from './reducer';
import * as services from '../services';
import * as selectors from './selectors';
import {
  Physician,
  PlansSuccess,
  Product,
  VoucherData,
  VoucherResponse,
} from '../services/types';
import {
  normalizeSubscriptionData,
  setAddonsForSingleUser,
} from './normalizer';
import { BillingInfo, PhysiciansByAddon, StepTypes } from './types';
import {
  getErrorsMessage,
  getResponseErrors,
  customizeErrorMessage,
  saveIsVoucherApplied,
} from 'features/checkout/utils';
import { dispatchGaEvent } from 'shared/utils/googleAnalytics';
import { resetTurnstile } from 'shared/utils/turnstile';
import {
  SIGNUP_PAYWALL_KEY,
  VOUCHER_STORAGE_KEY,
} from 'features/new-auth/stepSignup/constants';
import { toIsoDate } from '../utils/dateUtils';

type SelectAddonWorkerPayload = ReturnType<typeof actions.selectAddonTrigger>;
type SubscriptionWorkerPayload = ReturnType<typeof actions.triggerSubscription>;

export function* fetchCheckoutProductsWorker() {
  try {
    const { data: products }: Response<PlansSuccess> = yield call(
      services.getPlans,
    );

    yield put(actions.fetchCheckoutProductsSuccess({ products }));
  } catch (error) {
    yield put(actions.fetchCheckoutDataFailure());
  }
}

export function* fetchCheckoutDataWorker() {
  try {
    const clinicId: number = yield select(selectors.getClinicId);

    const { data: products }: Response<PlansSuccess> = yield call(
      services.getPlans,
    );
    const { data: physicians } = yield call(
      services.getPhysiciansByClinic,
      clinicId,
    );

    yield put(actions.fetchCheckoutDataSuccess({ products, physicians }));
    if (products.coupon?.code) {
      yield put(actions.fetchCheckoutCouponData({ coupon: products.coupon }));
    }
  } catch (error) {
    yield put(actions.fetchCheckoutDataFailure());
  }
}

export function* selectAddonWorker({
  payload: selectedAddon,
}: SelectAddonWorkerPayload) {
  const selectedAddons: Product[] = yield select(selectors.getSelectedAddons);
  const selectedAddonsCopy = [...selectedAddons];

  const index = selectedAddonsCopy.findIndex(
    (addon) => addon.id === selectedAddon.id,
  );

  if (index >= 0) {
    selectedAddonsCopy.splice(index, 1);
    yield put(actions.updateSelectedAddons(selectedAddonsCopy));
  } else {
    yield put(
      actions.updateSelectedAddons([...selectedAddonsCopy, selectedAddon]),
    );
  }
}

export function* subscriptionWorker({
  payload: subscriptionFormData,
}: SubscriptionWorkerPayload) {
  try {
    yield put(actions.updateSubscriptionStatus(RequestStatus.Pending));

    const billing: BillingInfo = yield select(selectors.getAddressData);
    const { person_type, billing_birthdate, ...billingPayload } = billing;

    const formattedBirthdate =
      person_type === 'pf' && billing_birthdate
        ? toIsoDate(billing_birthdate)
        : undefined;

    const isAnnual: boolean = yield select(selectors.getRecurrence);
    const selectedPlan: Product = yield select(selectors.getSelectedPlan);
    const physicians: Physician[] = yield select(selectors.getPhysicians);
    const physicianByAddon: PhysiciansByAddon | undefined = yield select(
      selectors.getPhysicianByAddon,
    );
    const selectedAddons: Product[] = yield select(selectors.getSelectedAddons);

    const hasSinglePhysician = physicians.length === 1;
    const addonsForSinglePhysician =
      hasSinglePhysician && selectedAddons.length > 0;
    const addons = addonsForSinglePhysician
      ? setAddonsForSingleUser({ selectedAddons, physicians })
      : physicianByAddon;

    const subscribeData = {
      ...normalizeSubscriptionData({
        isAnnual,
        physicians,
        selectedPlan,
        subscriptionFormData,
        physicianByAddon: addons,
      }),
      billing_info: {
        ...billingPayload,
        billing_country: 'BR',
        ...(formattedBirthdate && { billing_birthdate: formattedBirthdate }),
      },
    };

    yield call(services.postSubscribe, subscribeData);

    yield put(actions.updateSubscriptionStatus(RequestStatus.Success));
    yield put(actions.changeSubscriptionStep());
    yield call([localStorage, 'removeItem'], SIGNUP_PAYWALL_KEY);
    yield call(dispatchGaEvent, 'checkout_complete');
    yield call([localStorage, 'removeItem'], VOUCHER_STORAGE_KEY);
  } catch (error) {
    resetTurnstile();
    const errors = getResponseErrors(error as AxiosError);
    const errorsMessage = getErrorsMessage(errors);
    yield put(actions.updateSubscriptionStatus(RequestStatus.Error));
    yield put(actions.setErrorsMessage(errorsMessage));
  }
}

const MIN_PHYSICIANS_COUNT = 2;

export function* verifyStepBeginWorker() {
  const physicians: Physician[] = yield select(selectors.getPhysicians);
  const selectedAddons: Product[] = yield select(selectors.getSelectedAddons);

  const stepMapper: string[] = [];

  if (selectedAddons?.length && physicians?.length >= MIN_PHYSICIANS_COUNT) {
    stepMapper.push(...selectedAddons.map((addon) => addon.slug));
  }

  stepMapper.push(StepTypes.ADDRESS, StepTypes.PAYMENT, StepTypes.SUCCESS);

  const initialStep = stepMapper[0];

  yield put(actions.updateSubscriptionStepMapper(stepMapper));
  yield put(actions.updateCurrentSubscriptionStep(initialStep));
}

export function* changeSubscriptionStepWorker() {
  const stepMapper: string[] = yield select(
    selectors.getSubscriptionStepMapper,
  );
  const currentStep: string = yield select(
    selectors.getCurrentSubscriptionStep,
  );

  const indexOfCurrentStep = stepMapper.findIndex(
    (step) => step === currentStep,
  );
  const nextStep = stepMapper[indexOfCurrentStep + 1];

  yield put(actions.updateCurrentSubscriptionStep(nextStep));
}

export function* handleApplyVoucherWorker(action: PayloadAction<VoucherData>) {
  try {
    const { voucher } = action.payload;
    const response: Response<VoucherResponse> = yield call(
      services.postApplyVoucher,
      { voucher },
    );
    const { data: products }: Response<PlansSuccess> = yield call(
      services.getPlans,
    );

    if (response.status) {
      yield put(actions.applyVoucherSuccess(voucher));
      yield put(actions.setIsVoucherOrCouponApplied(true));
      saveIsVoucherApplied('true');
    } else {
      const errorMessage = customizeErrorMessage(response.status);
      yield put(actions.applyVoucherFailure(errorMessage));
    }

    yield put(actions.fetchCheckoutProductsSuccess({ products }));
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = customizeErrorMessage(
      axiosError.response?.status ?? 500,
    );
    yield put(actions.applyVoucherFailure(errorMessage));
    yield put(actions.fetchCheckoutDataFailure());
  }
}

export function* handleRemoveVoucherWorker(action: PayloadAction<VoucherData>) {
  try {
    const { voucher } = action.payload;
    const response: Response<VoucherResponse> = yield call(
      services.postRemoveVoucher,
      { voucher },
    );
    const { data: products }: Response<PlansSuccess> = yield call(
      services.getPlans,
    );

    if (response.status) {
      yield put(actions.removeVoucherSuccess());
      yield put(actions.setIsVoucherOrCouponApplied(false));
    } else {
      const errorMessage = customizeErrorMessage(response.status);
      yield put(actions.removeVoucherFailure(errorMessage));
    }

    yield call([localStorage, 'removeItem'], VOUCHER_STORAGE_KEY);
    yield put(actions.fetchCheckoutProductsSuccess({ products }));
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = customizeErrorMessage(
      axiosError.response?.status ?? 500,
    );
    yield put(actions.removeVoucherFailure(errorMessage));
    yield put(actions.fetchCheckoutDataFailure());
  }
}

export default function* plansSagas() {
  yield takeLatest(actions.fetchCheckoutProducts, fetchCheckoutProductsWorker);
  yield takeLatest(actions.fetchCheckoutData, fetchCheckoutDataWorker);
  yield takeLatest(actions.selectAddonTrigger, selectAddonWorker);
  yield takeLatest(actions.triggerSubscription, subscriptionWorker);
  yield takeLatest(actions.triggerVerifyInitialStep, verifyStepBeginWorker);
  yield takeLatest(
    actions.changeSubscriptionStep,
    changeSubscriptionStepWorker,
  );
  yield takeLatest(actions.applyVoucher, handleApplyVoucherWorker);
  yield takeLatest(actions.removeVoucher, handleRemoveVoucherWorker);
}
